// Логиката на редовете на листа „Прехвърляне": наличност/партиди КЪМ ДАТАТА
// от източника (лениво, per продукт), FIFO себестойност, „Остава" в
// източника и „Става" в приемника, суми, редовете над наличното и payload-ът.
// Решетката и картите само рисуват изчислените редове.
import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import {
  getStockAsOf,
  type Lot,
  type Product,
  type StockBalance,
} from "../../../../api/inventory";
import { parseDecimal } from "../../components/numbers";
import { clampDecimals } from "../../components/qty";
import { fifoUnitPrice, type TransferLineDraft } from "./transferWorksheetShared";

// Изчисленият ред: чернова + всичко производно за показване и за валидация
export type TransferComputedRow = {
  productId: number;
  product: Product | undefined;
  unit: string | null;
  decimals: number;
  draft: TransferLineDraft;
  quantity: number;
  available: number | null; // null = наличността още се зарежда
  lots: Lot[];
  unitPrice: number | null;
  net: number;
  gross: number;
  remainingInSource: number | null; // „Остава" в източника
  resultingInTarget: number | null; // „Става" в приемника
  overStock: boolean;
};

type RowsInput = {
  lines: TransferLineDraft[];
  products: Product[];
  fromWarehouseId: number | null;
  date: Dayjs | null;
  targetBalances: StockBalance[];
  vatRegistered: boolean;
  // Редакция: количествата от ЗАПИСАНИЯ документ (per продукт) + датата му.
  // Наличността към датата вече е намалена с тях — без да се добавят обратно,
  // документът се проверява все едно стоката му е излязла два пъти.
  savedQuantities?: Map<number, number> | null;
  savedDate?: string | null; // YYYY-MM-DD
};

export const useTransferRowsActions = ({
  lines,
  products,
  fromWarehouseId,
  date,
  targetBalances,
  vatRegistered,
  savedQuantities = null,
  savedDate = null,
}: RowsInput) => {
  const productById = useMemo(
    () => new Map(products.map((product) => [product.id, product])),
    [products],
  );
  const targetQuantityByProduct = useMemo(
    () =>
      new Map(
        targetBalances.map((balance) => [balance.productId, balance.quantity]),
      ),
    [targetBalances],
  );

  // Наличност + партиди от ИЗТОЧНИКА към ИЗБРАНАТА ДАТА — лениво, само за
  // продуктите в редовете (задно-датирано прехвърляне вижда какво е имало тогава).
  const asOfDay = date ? date.format("YYYY-MM-DD") : null;
  const lineProductIds = useMemo(
    () => lines.map((line) => line.productId),
    [lines],
  );
  const asOfQueries = useQueries({
    queries: lineProductIds.map((productId) => ({
      queryKey: ["inventory", "stock-as-of", fromWarehouseId, productId, asOfDay],
      queryFn: () =>
        getStockAsOf(fromWarehouseId as number, productId, asOfDay as string),
      enabled: !!fromWarehouseId && !!asOfDay,
    })),
  });
  // Стабилен подпис на заявките — иначе memo-то се преизчислява на всеки рендер
  const asOfSignature = asOfQueries.map((query) => query.dataUpdatedAt).join("|");
  const asOfByProduct = useMemo(() => {
    const map = new Map<number, { quantity: number; lots: Lot[] }>();
    lineProductIds.forEach((productId, index) => {
      const data = asOfQueries[index]?.data;
      if (data)
        map.set(productId, { quantity: data.quantity, lots: data.lots ?? [] });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineProductIds, asOfSignature]);

  const rows = useMemo<TransferComputedRow[]>(() => {
    // Един продукт може да е само на един ред
    return lines.map((draft) => {
      const product = productById.get(draft.productId);
      const decimals = clampDecimals(product?.decimalPlaces);
      const quantity = parseDecimal(draft.quantity);
      const asOf = asOfByProduct.get(draft.productId);
      // При редакция връщаме обратно собственото записано количество — само
      // ако избраната дата не е ПРЕДИ документа (тогава наличността към
      // датата още не го е приспаднала).
      const savedQuantity =
        savedQuantities != null &&
        savedDate != null &&
        asOfDay != null &&
        asOfDay >= savedDate
          ? (savedQuantities.get(draft.productId) ?? 0)
          : 0;
      // Отрицателна наличност се чете като 0 (както навсякъде в модула)
      const available = asOf ? Math.max(0, asOf.quantity) + savedQuantity : null;
      const lots = asOf?.lots ?? [];
      const unitPrice = fifoUnitPrice(lots, quantity, draft.lotId);
      const net = unitPrice != null && quantity > 0 ? quantity * unitPrice : 0;
      const vatRate = product?.vatRate ?? 0;
      const gross = vatRegistered && vatRate ? net * (1 + vatRate / 100) : net;
      const targetQuantity = targetQuantityByProduct.get(draft.productId);
      return {
        productId: draft.productId,
        product,
        unit: product?.unit ?? null,
        decimals,
        draft,
        quantity,
        available,
        lots,
        unitPrice,
        net,
        gross,
        remainingInSource: available == null ? null : available - quantity,
        // Без избран приемник няма баланси → показваме само прехвърляното
        resultingInTarget:
          targetQuantity != null
            ? Math.max(0, targetQuantity) + quantity
            : quantity,
        overStock: available != null && quantity > available,
      };
    });
  }, [
    lines,
    productById,
    asOfByProduct,
    targetQuantityByProduct,
    vatRegistered,
    savedQuantities,
    savedDate,
    asOfDay,
  ]);

  const totalNet = rows.reduce((sum, row) => sum + row.net, 0);
  const totalGross = rows.reduce((sum, row) => sum + row.gross, 0);
  const filledRows = rows.filter((row) => row.quantity > 0);
  const overStockNames = rows
    .filter((row) => row.overStock)
    .map((row) => row.product?.name ?? `#${row.productId}`);

  const buildLines = () =>
    filledRows.map((row) => ({
      productId: row.productId,
      quantity: row.quantity,
      mode: (row.draft.lotId ? "lot" : "fifo") as "fifo" | "lot",
      lotId: row.draft.lotId,
    }));

  return { rows, totalNet, totalGross, filledRows, overStockNames, buildLines };
};
